import { http } from '@/lib/http';
import type {
  FlightMonitorFilters,
  ICreateCaseResult,
  IFlightMonitorRow,
  IFlightsMonitorPage,
} from './types';

// Flight helpers shared by the operator surfaces. The PNR override picker does NOT browse
// the airline schedule (a schedule row has no seat to sell, contract
// `airline-air-ticketing.v1`): it lists provider offers for the group — see
// `searchGroupFlightOffers` in `@/lib/rebooking/api`.

/** A schedule row as the operator-bff shapes it (carrier + bare or qualified number). */
export interface FlightCandidate {
  flightUrn: string;
  carrierCode: string;
  flightNumber: string;
  scheduledDate: string;
  origin: string;
  destination: string;
  paxCount: number;
  aircraftType?: string;
  /** Real departure instant (ISO) — feeds OverrideRequest.departureAt. */
  scheduledDepartureUtc?: string;
  status?: string;
}

/** IATA carrier code → display name for the picker / override surfaces. An
 *  unknown code renders as the bare code — still unambiguous. */
const CARRIER_NAMES: Record<string, string> = {
  H2: 'SKY Airline',
  EY: 'Etihad Airways',
  WN: 'Southwest Airlines',
  IB: 'Iberia',
  PU: 'Plus Ultra',
  AN: 'Advanced Air',
  MK: 'Air Mauritius',
  LA: 'LATAM Airlines',
  G3: 'GOL Linhas Aéreas',
  AV: 'Avianca',
  AR: 'Aerolíneas Argentinas',
  JA: 'JetSMART',
  CM: 'Copa Airlines',
  AM: 'Aeroméxico',
};

export function carrierName(code: string | undefined): string | undefined {
  return code ? CARRIER_NAMES[code.toUpperCase()] : undefined;
}

/** Wire form of a picked flight ("H2102") — the override endpoint parses the
 *  carrier prefix for vendor validation, so a bare number must not travel alone
 *  (it used to fall back to the LATAM carrier and 422 on other tenants). */
export function flightWireNumber(c: Pick<FlightCandidate, 'carrierCode' | 'flightNumber'>): string {
  const n = c.flightNumber.trim();
  return /^\d+$/.test(n) && c.carrierCode ? `${c.carrierCode}${n}` : n;
}

// ── Flights MONITOR (NEX-342 / F4) ─────────────────────────────────────────────
// The operator's current-flights view: scoped, status + risk + case/disruption
// marks, keyset-paginated.

/** `GET /v1/flights` — the operator's scoped, marked, paginated current flights.
 *  FROZEN cursor contract (`?…&cursor&limit` → `{ items, nextCursor }`, no `total`);
 *  the cursor is opaque (echo a prior page's `nextCursor` back verbatim). `limit`
 *  is left to the BFF default (50, hard max 200) unless overridden. */
export async function listFlights(
  filters: FlightMonitorFilters,
  cursor?: string,
): Promise<IFlightsMonitorPage> {
  const sp = new URLSearchParams();
  if (filters.airports) sp.set('airports', filters.airports);
  if (filters.direction) sp.set('direction', filters.direction);
  if (filters.status) sp.set('status', filters.status);
  if (filters.q) sp.set('q', filters.q);
  if (filters.from) sp.set('from', filters.from);
  if (filters.to) sp.set('to', filters.to);
  if (filters.disrupted) sp.set('disrupted', '1');
  if (filters.board) sp.set('board', filters.board);
  if (cursor) sp.set('cursor', cursor);
  const qs = sp.toString();
  return http.get(qs ? `v1/flights?${qs}` : 'v1/flights').json<IFlightsMonitorPage>();
}

/** `GET /v1/flights/:flightUrn` — ONE flight by URN: real schedule + FIDS status
 *  + risk + case/disruption marks (the Express board header's read). The BFF
 *  degrades open to a urn-derived row (code + instant, no status) when the
 *  schedule can't be read — render what comes back, don't gate on `status`. */
export async function getFlight(flightUrn: string): Promise<IFlightMonitorRow> {
  return http.get(`v1/flights/${encodeURIComponent(flightUrn)}`).json<IFlightMonitorRow>();
}

/** `POST /v1/flights/:flightUrn/case` — one-click create-case-from-flight. No body
 *  needed (the BFF defaults the disruption reason to MANUAL). Returns the new case
 *  URN, or the pre-existing one on a dedup race (`created:false`). */
export async function createCaseFromFlight(flightUrn: string): Promise<ICreateCaseResult> {
  return http
    .post(`v1/flights/${encodeURIComponent(flightUrn)}/case`)
    .json<ICreateCaseResult>();
}
